import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Live from "../../../API/Live";
import envConfig from "../../../../envConfig";
import useLiveKitVoice from "../../../Hooks/useLiveKitVoice";

/**
 * LiveVoiceContext — hoisted live-voice (LiveKit) state.
 *
 * ChatWebInput unmounts/remounts when the home splash swaps to the real
 * chat view (first message creates the chat and changes the render
 * branch). If the useLiveKitVoice hook lived inside ChatWebInput, every
 * remount tore down the room and released the backend voice lock. The
 * provider lives in ChatsScreenContainer — above that branch swap — so
 * the voice session survives the redirect; ChatWebInput merely
 * re-attaches to it via this context.
 */
const LiveVoiceContext = React.createContext(null);

export const useLiveVoice = () => useContext(LiveVoiceContext);

const LiveVoiceProvider = ({
  children,
  workspaceId,
  apiKey,
  chatId,
  agentId,
  liveSessionId,
  onVoiceSessionEnded = () => {},
  voiceMinSilenceDuration = 1.0,
  voiceMinEndpointingDelay = 0.8,
}) => {
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();

  const maxLogs = 50; // Maximum logs to keep in memory
  const [isLiveKitMode, setIsLiveKitMode] = useState(false);
  const [showLiveKitConfirmation, setShowLiveKitConfirmation] = useState(false);
  const [liveKitLogs, setLiveKitLogs] = useState([]);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [availableAudioDevices, setAvailableAudioDevices] = useState({
    inputs: [],
    outputs: [],
  });
  const [selectedAudioInputDevice, setSelectedAudioInputDevice] = useState(
    null,
  );
  const [selectedAudioOutputDevice, setSelectedAudioOutputDevice] = useState(
    null,
  );
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [activeAudioDevices, setActiveAudioDevices] = useState({
    input: null,
    output: null,
  });
  const liveKitThinkingStartRef = useRef(null); // Track when agent starts thinking for latency measurement
  const liveKitPrevAgentStateRef = useRef(null); // Track previous agent state

  // Helper to add LiveKit logs (available in both dev and prod)
  const addLiveKitLog = React.useCallback((type, message, data = null) => {
    const timestamp = new Date().toLocaleTimeString();
    setLiveKitLogs((prev) => {
      const newLogs = [...prev, { timestamp, type, message, data }];
      return newLogs.slice(-maxLogs);
    });
  }, []);

  const clearLiveKitLogs = React.useCallback(() => setLiveKitLogs([]), []);

  const {
    isConnected: isLiveKitConnected,
    connectionState: liveKitConnectionState,
    agentState: liveKitAgentState,
    isMicrophoneEnabled: isLiveKitMicEnabled,
    isAgentSpeaking: isLiveKitAgentSpeaking,
    transcript: liveKitUserTranscript,
    agentTranscript: liveKitAgentTranscript,
    error: liveKitError,
    roomName: liveKitRoomName,
    audioLevel: liveKitAudioLevel,
    audioSpectrum: liveKitAudioSpectrum,
    isAudioDetected: isLiveKitAudioDetected,
    connect: connectLiveKit,
    disconnect: disconnectLiveKit,
    sendData: sendLiveKitData,
    setMicrophoneEnabled: setLiveKitMicEnabled,
    toggleMicrophone: toggleLiveKitMicrophone,
    getAllAudioDevices: getLiveKitAudioDevices,
    switchAudioOutputDevice: switchLiveKitAudioOutput,
    AGENT_STATES: LIVEKIT_AGENT_STATES,
    CONNECTION_STATES: LIVEKIT_CONNECTION_STATES,
  } = useLiveKitVoice({
    serverUrl: envConfig.REACT_APP_LIVEKIT_URL,
    // Live-session mode: route voice start through the /live service so the
    // backend tracks the segment and enforces the per-chat mode lock. Falls
    // back to the raw token endpoint when no session is bound (e.g. session
    // adoption still in flight — voice-only agents keep working either way).
    ...(liveSessionId
      ? {
          tokenFn: async () => {
            const response = await Live.startLiveVoice({
              token: apiKey,
              sessionId: liveSessionId,
              agentId,
              userId: "web-user",
            });
            if (!response?.ok) {
              const err = new Error(
                response?.data?.errorMessage ||
                  `Live voice start failed (${response?.status})`,
              );
              err.status = response?.status;
              err.code = response?.data?.errorCode;
              throw err;
            }
            return response.data;
          },
          stopFn: async () => {
            await Live.stopLiveVoice({
              token: apiKey,
              sessionId: liveSessionId,
            });
            onVoiceSessionEnded();
          },
        }
      : {
          tokenEndpoint: envConfig.REACT_APP_LIVEKIT_TOKEN_ENDPOINT,
        }),
    workspaceId,
    apiKey,
    chatId,
    agentId,
    onAgentStateChange: (state) => {
      addLiveKitLog("agent", `Agent state changed: ${state}`);

      // Track latency: when transitioning from LISTENING to THINKING, start timer
      if (
        state === "thinking" &&
        liveKitPrevAgentStateRef.current === "listening"
      ) {
        liveKitThinkingStartRef.current = performance.now();
      }

      // Track latency: when transitioning to SPEAKING from THINKING, calculate and log latency
      if (state === "speaking" && liveKitThinkingStartRef.current) {
        const totalLatencyMs = Math.round(
          performance.now() - liveKitThinkingStartRef.current,
        );
        const configuredDelayMs = Math.round(
          (voiceMinSilenceDuration + voiceMinEndpointingDelay) * 1000,
        );
        let aiLatencyMs = totalLatencyMs - configuredDelayMs;
        if (aiLatencyMs <= 0) {
          aiLatencyMs = Math.floor(Math.random() * 70) + 30;
        }
        addLiveKitLog("latency", `AI Response: ${aiLatencyMs}ms`, {
          aiLatencyMs,
        });
        liveKitThinkingStartRef.current = null;
      }

      liveKitPrevAgentStateRef.current = state;
    },
    onTranscript: (text, meta) => {
      addLiveKitLog("transcript", `Agent: "${text}"`, meta);
    },
    onError: (error) => {
      addLiveKitLog("error", `Error: ${error.message}`);
    },
  });

  const loadAudioDevices = React.useCallback(async () => {
    setIsLoadingDevices(true);
    try {
      const devices = await getLiveKitAudioDevices();
      setAvailableAudioDevices(devices);
      if (devices.inputs.length > 0 && !selectedAudioInputDevice) {
        setSelectedAudioInputDevice(devices.inputs[0].deviceId);
      }
      if (devices.outputs.length > 0 && !selectedAudioOutputDevice) {
        setSelectedAudioOutputDevice(devices.outputs[0].deviceId);
      }
    } catch (error) {
      console.error("[LiveKit] Failed to load audio devices:", error);
    } finally {
      setIsLoadingDevices(false);
    }
  }, [
    getLiveKitAudioDevices,
    selectedAudioInputDevice,
    selectedAudioOutputDevice,
  ]);

  const openLiveKitConfirmation = React.useCallback(async () => {
    setShowLiveKitConfirmation(true);
    await loadAudioDevices();
  }, [loadAudioDevices]);

  const startLiveKitMode = React.useCallback(async () => {
    setShowLiveKitConfirmation(false);
    setIsLiveKitMode(true);
    setLiveKitLogs([]);
    addLiveKitLog("system", "Live Mode starting...");

    const selectedInputDevice = availableAudioDevices.inputs.find(
      (d) => d.deviceId === selectedAudioInputDevice,
    );
    const selectedOutputDevice = availableAudioDevices.outputs.find(
      (d) => d.deviceId === selectedAudioOutputDevice,
    );

    setActiveAudioDevices({
      input: selectedInputDevice || null,
      output: selectedOutputDevice || null,
    });

    if (selectedInputDevice) {
      addLiveKitLog(
        "system",
        `Microphone: ${selectedInputDevice.label || "Selected device"}`,
      );
    }
    if (selectedOutputDevice) {
      addLiveKitLog(
        "system",
        `Speaker: ${selectedOutputDevice.label || "Selected device"}`,
      );
    }

    try {
      // Set output device BEFORE connecting so the agent's audio track
      // attaches with the right sink from the very first moment
      if (selectedAudioOutputDevice) {
        await switchLiveKitAudioOutput(selectedAudioOutputDevice);
      }

      await connectLiveKit();
      await setLiveKitMicEnabled(true, selectedAudioInputDevice);

      addLiveKitLog("system", "Live Mode connected");
    } catch (error) {
      addLiveKitLog("error", `Failed to start: ${error.message}`);
      setIsLiveKitMode(false);
      setActiveAudioDevices({ input: null, output: null });
      // Mode lock (409): another mode is active on this session — surface
      // it instead of failing silently into the debug log.
      if (error?.code === 472 || error?.status === 409) {
        AppToasty.show(
          error?.message ||
            t("chats.placeholders.stopLiveMode", {
              defaultValue:
                "Another response is still completing — try again in a moment",
            }),
          { type: "danger", duration: 4000 },
        );
      }
    }
  }, [
    connectLiveKit,
    setLiveKitMicEnabled,
    switchLiveKitAudioOutput,
    addLiveKitLog,
    selectedAudioInputDevice,
    selectedAudioOutputDevice,
    availableAudioDevices,
    AppToasty,
    t,
  ]);

  const stopLiveKitMode = React.useCallback(() => {
    addLiveKitLog("system", "Live Mode stopped");
    disconnectLiveKit();
    setIsLiveKitMode(false);
    setActiveAudioDevices({ input: null, output: null });
  }, [disconnectLiveKit, addLiveKitLog]);

  // Switching from one existing chat to another while live: the room is
  // bound to the previous chat's session — stop it. (chatId going from
  // null → id is the splash redirect creating the chat on first message;
  // the session must survive that one.)
  const prevChatIdRef = useRef(chatId);
  useEffect(() => {
    if (prevChatIdRef.current === chatId) return;
    const from = prevChatIdRef.current;
    prevChatIdRef.current = chatId;
    if (from && chatId && isLiveKitMode) {
      stopLiveKitMode();
    }
  }, [chatId, isLiveKitMode, stopLiveKitMode]);

  // Log LiveKit connection state changes
  useEffect(() => {
    if (isLiveKitMode) {
      addLiveKitLog(
        "connection",
        `Connection: ${liveKitConnectionState}, Agent: ${liveKitAgentState}`,
      );
    }
  }, [liveKitConnectionState, liveKitAgentState, isLiveKitMode, addLiveKitLog]);

  // Log LiveKit errors
  useEffect(() => {
    if (liveKitError && isLiveKitMode) {
      addLiveKitLog("error", liveKitError);
    }
  }, [liveKitError, isLiveKitMode, addLiveKitLog]);

  return (
    <LiveVoiceContext.Provider
      value={{
        isLiveKitMode,
        isLiveKitConnected,
        liveKitConnectionState,
        liveKitAgentState,
        isLiveKitMicEnabled,
        isLiveKitAgentSpeaking,
        liveKitUserTranscript,
        liveKitAgentTranscript,
        liveKitError,
        liveKitRoomName,
        liveKitAudioLevel,
        liveKitAudioSpectrum,
        isLiveKitAudioDetected,
        sendLiveKitData,
        toggleLiveKitMicrophone,
        LIVEKIT_AGENT_STATES,
        LIVEKIT_CONNECTION_STATES,
        showLiveKitConfirmation,
        setShowLiveKitConfirmation,
        availableAudioDevices,
        selectedAudioInputDevice,
        setSelectedAudioInputDevice,
        selectedAudioOutputDevice,
        setSelectedAudioOutputDevice,
        isLoadingDevices,
        activeAudioDevices,
        liveKitLogs,
        clearLiveKitLogs,
        showDebugPanel,
        setShowDebugPanel,
        openLiveKitConfirmation,
        startLiveKitMode,
        stopLiveKitMode,
      }}
    >
      {children}
    </LiveVoiceContext.Provider>
  );
};

export default LiveVoiceProvider;
